import { IMAGE_MANIFEST } from '../data/imageManifest.js';

/**
 * Lookups into the build-time image manifest (generated by
 * scripts/optimize-images.mjs), which lists the widths every original under
 * /images/ was resized to. Components hand in the original path and get the
 * optimized variants back, so this is the only file in src/ that knows where
 * the variants live and how they are named. The script writes them under the
 * same names, so change the two together.
 */

// URL path of public/images/optimized/, where scripts/optimize-images.mjs
// writes each variant as {stem}-{width}.{format}.
const OPTIMIZED_URL_BASE = '/images/optimized';

/** '/images/foo.jpg' → 'foo', the key the manifest is indexed by. */
function stemOf(src) {
  const file = src.split('/').pop() || '';
  const dot = file.lastIndexOf('.');
  return dot === -1 ? file : file.slice(0, dot);
}

/**
 * The manifest entry for an original image path, or undefined when the image
 * has no optimized variants (a freshly added asset, or an entry without any
 * widths). Callers then fall back to the original file.
 */
export function manifestEntryFor(src) {
  const entry = IMAGE_MANIFEST[stemOf(src)];
  return entry?.widths?.length ? entry : undefined;
}

/** URL of one optimized variant, e.g. variantUrl('foo', 800, 'webp'). */
export function variantUrl(stem, width, format) {
  return `${OPTIMIZED_URL_BASE}/${stem}-${width}.${format}`;
}

/**
 * URL of the WebP variant at the smallest width that still covers
 * `targetWidth` (the largest width when none does), or null when the image has
 * no variants. `targetWidth` is in image pixels: callers scale CSS widths by
 * the device pixel ratio themselves.
 */
export function webpVariantFor(src, targetWidth) {
  const entry = manifestEntryFor(src);
  if (!entry) return null;
  const { stem, widths } = entry;
  // The script lists widths smallest first, so the first match is the smallest.
  const width = widths.find((w) => w >= targetWidth) ?? widths[widths.length - 1];
  return variantUrl(stem, width, 'webp');
}
